import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Spinner,
  Table,
  Label,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import RoleService from '../../../Api/services/role.service';
import PermissionService from '../../../Api/services/permission.service';
import type { components } from '../../../Api/types/openapi';
import { useTranslation } from 'react-i18next';
import { Check, Trash2 } from 'react-feather';

type RoleResponse = components['schemas']['RoleResponse'];
type Permission = components['schemas']['Permission'];
type PermissionResponse = components['schemas']['PermissionResponse'];

interface RolePermissionsModalProps {
  isOpen: boolean;
  toggle: () => void;
  role: RoleResponse | null;
  onSuccess: () => void;
}

const RolePermissionsModal: React.FC<RolePermissionsModalProps> = ({
  isOpen,
  toggle,
  role,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && role) {
      loadPermissions();
      loadRolePermissions();
    }
  }, [isOpen, role]);

  const loadPermissions = async () => {
    try {
      setLoadingPermissions(true);
      const data = await PermissionService.getAllPermissions({ name: searchTerm });
      setPermissions(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar permisos');
      toast.error(errorMessage);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const loadRolePermissions = async () => {
    if (!role?.id) return;

    try {
      const roleData = await RoleService.getRoleById(role.id);
      const permissionIds = roleData.permissions?.map((p: PermissionResponse) => p.id) || [];
      setRolePermissions(permissionIds);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar permisos del rol');
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (isOpen && role) {
      loadPermissions();
    }
  }, [searchTerm]);

  const handleTogglePermission = async (permissionId: number) => {
    if (!role?.id) return;

    const hasPermission = rolePermissions.includes(permissionId);

    try {
      setLoading(true);
      if (hasPermission) {
        await RoleService.removePermissionFromRole({
          id: role.id,
          permission: permissionId,
        });
        setRolePermissions(rolePermissions.filter((id) => id !== permissionId));
        toast.success(t('Permiso eliminado del rol'));
      } else {
        await RoleService.addPermissionToRole({
          id: role.id,
          permission: permissionId,
        });
        setRolePermissions([...rolePermissions, permissionId]);
        toast.success(t('Permiso agregado al rol'));
      }
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al actualizar permisos');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredPermissions = permissions.filter((permission) =>
    permission.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    permission.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {t('Gestionar Permisos')} - {role?.name}
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <Input
            type="text"
            placeholder={t('Buscar permisos...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {loadingPermissions ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Table striped>
              <thead>
                <tr>
                  <th>{t('Nombre')}</th>
                  <th>{t('Código')}</th>
                  <th>{t('Estado')}</th>
                  <th>{t('Acción')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredPermissions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center">
                      {t('No se encontraron permisos')}
                    </td>
                  </tr>
                ) : (
                  filteredPermissions.map((permission) => {
                    const hasPermission = rolePermissions.includes(permission.id || 0);
                    return (
                      <tr key={permission.id}>
                        <td>{permission.name}</td>
                        <td>
                          <code>{permission.code || '-'}</code>
                        </td>
                        <td>
                          <div className="form-check form-switch">
                            <Input
                              type="checkbox"
                              className="form-check-input"
                              checked={permission.active || false}
                              readOnly
                            />
                            <Label className="form-check-label">
                              {permission.active ? t('Activo') : t('Inactivo')}
                            </Label>
                          </div>
                        </td>
                        <td>
                          <Btn
                            color={hasPermission ? 'danger' : 'success'}
                            size="sm"
                            className="btn_list_action"
                            onClick={() => handleTogglePermission(permission.id || 0)}
                            disabled={loading || !permission.active}
                            title={hasPermission ? t('Quitar') : t('Agregar')}
                          >
                            {hasPermission ? (
                              <Trash2 size={14} />
                            ) : (
                              <Check size={14} />
                            )}
                          </Btn>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Btn color="secondary" onClick={toggle} disabled={loading}>
          {t('Cerrar')}
        </Btn>
      </ModalFooter>
    </Modal>
  );
};

export default RolePermissionsModal;

